import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import IndividualPricingComparison, { firstPaydayAfter } from '../../pages/admin/IndividualPricingComparison';
import { capturedAffordability } from '../../../services/individualAffordability';
import type { ComparisonInput } from '../../../services/individualPricingComparison';
import IndividualErrorAlert from '../IndividualErrorAlert';
import { QuotationContext, individualQuotationContext, IndividualPricingQuote, quotationError } from '../../../services/individualPricingQuotations';

/** The application has one current quotation; staff do not choose a pricing scenario. */
export default function IndividualApplicationQuotation({ loan, onUse, bureauRevision, showAffordability = true }: { loan: any; onUse: (quote: IndividualPricingQuote) => void; bureauRevision?: string; showAffordability?: boolean }) {
  const { control, setValue } = useFormContext();
  const values = useWatch({ control });
  const applicantId = values?.individual?.idNumber || '';
  const financial = capturedAffordability(values);
  const [reload, setReload] = useState(0);
  const [context, setContext] = useState<{key:string; pricing?:QuotationContext; error?:string}>();
  const count = loan.customLoanType === 'ONCE_OFF' ? 1 : Number(loan.numberOfInstallments);
  const ready = !!applicantId && !!loan.commencementDate && Number(loan.loanAmount) > 0
    && Number(loan.loanAmount) <= 8000 && ['ONCE_OFF','INSTALLMENTS'].includes(loan.customLoanType)
    && Number.isInteger(count) && count >= 1 && count <= 6;
  const contextKey = JSON.stringify({applicantId,date:loan.commencementDate,ready,reload});
  const retainedSchedule = useRef(loan.individualPricingInputs?.paydaySchedule || loan.individualPricingQuote?.inputs.paydaySchedule);
  const retainedQuote = useRef<IndividualPricingQuote | undefined>(loan.individualPricingQuote);
  const qualification = loan.individualQualificationInputs?.paydaySchedule;
  const previous = loan.individualPricingInputs?.paydaySchedule || retainedSchedule.current;
  const payday = loan.individualQualificationSettings?.payday || qualification?.payday || previous?.payday;
  const missingDay = loan.individualQualificationSettings?.missingDay || qualification?.missingDayRule || previous?.missingDayRule || 'REJECT';
  const clearQuotation = useCallback(() => {
    ['individualPricingQuote','individualAffordabilityPreview','interestRate','interestAmount','adminFee','totalRepayment']
      .forEach(field => setValue(`loan.${field}`, undefined));
  }, [setValue]);
  const onStatus = useCallback((status:'incomplete'|'calculating'|'ready'|'error') => setValue('loan.individualQuotationState',status), [setValue]);
  useEffect(() => {
    let current = true;
    clearQuotation(); setContext(undefined);
    onStatus(ready ? 'calculating' : 'incomplete');
    if (!ready) return;
    individualQuotationContext(applicantId,loan.commencementDate).then(pricing => {
      if (current) setContext({key:contextKey,pricing});
    }).catch(failure => {
      if (current) { setContext({key:contextKey,error:quotationError(failure)}); onStatus('error'); }
    });
    return () => { current=false; };
  }, [contextKey,clearQuotation,onStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const onInputs = useCallback((input: ComparisonInput | undefined) => {
    clearQuotation(); setValue('loan.individualPricingInputs',input);
  }, [setValue,clearQuotation]);
  const onResult = useCallback((result: any) => setValue('loan.individualAffordabilityPreview',result), [setValue]);
  const current = context?.key === contextKey ? context : undefined;
  const pricing = current?.pricing;
  if (!ready) return <p role="status">Complete the applicant, loan amount, payment type and disbursement date. The quotation will calculate automatically.</p>;
  if (current?.error) return <section><IndividualErrorAlert message={current.error} />
    <button type="button" className="btn btn-outline-primary" onClick={()=>setReload(value=>value+1)}>Retry quotation</button></section>;
  if (!pricing) return <p role="status">Loading pricing for your quotation…</p>;
  const firstDate = previous?.advanceDate === loan.commencementDate && Number(previous?.payday) === Number(payday)
      && previous?.missingDayRule === missingDay ? previous.firstDueDate
    : firstPaydayAfter(loan.commencementDate,String(payday || ''),missingDay);
  return <IndividualPricingComparison id={pricing.pricing.policyId} revision={pricing.pricing.revision}
    initialScenario={{amount:String(loan.loanAmount),date:loan.commencementDate,count:String(count),
      payday:payday ? String(payday) : undefined,missingDay,firstDate}}
    applicationPricing={{version:pricing.pricing.versionId,applicantId,idType:values.individual?.idType,context:pricing,
      financial:showAffordability ? financial : undefined,onInputs,onResult,onStatus,bureauRevision,onUse,initialQuote:retainedQuote.current,
      onReload:()=>setReload(value=>value+1)}}
    onClose={()=>{}} />;
}
